<script setup lang="ts">
// Root shell: picks the current screen (DESIGN.md section 9.1, no router library — src/store/nav.ts),
// starts the SSE table stream once (section 9.2), wires the global badge scanner (section 9.4 —
// mounted exactly once here, so it's live on every screen), and shows the backend-unreachable
// banner + the global scan-feedback toast stack (ToastStack.vue / store/toast.ts). NavHeader is
// hidden on Live/New match so those screens keep full focus at 960x540.
import { computed, onMounted, onUnmounted, watch } from 'vue'
import Home from './views/Home.vue'
import NewMatch from './views/NewMatch.vue'
import LiveMatch from './views/LiveMatch.vue'
import PastMatches from './views/PastMatches.vue'
import PastMatchDetail from './views/PastMatchDetail.vue'
import Players from './views/Players.vue'
import Versus from './views/Versus.vue'
import Player from './views/Player.vue'
import Tournaments from './views/Tournaments.vue'
import TournamentDetail from './views/TournamentDetail.vue'
import VoiceTraining from './views/VoiceTraining.vue'
import DebugStt from './views/DebugStt.vue'
import DebugTts from './views/DebugTts.vue'
import NavHeader from './components/NavHeader.vue'
import BackendBanner from './components/BackendBanner.vue'
import ToastStack from './components/ToastStack.vue'
import ConfirmClaimPrompt from './components/ConfirmClaimPrompt.vue'
import { nav, navigate, replaceScreen } from './store/nav'
import { startTableStream, tableStore } from './store/table'
import { useGlobalScanner } from './composables/useGlobalScanner'
import { sounds } from './sounds'
import { onSttCommand, type SttCommandEvent } from './stt'
import { pushToast } from './store/toast'
import { setSide } from './store/draft'
import { resolveCornerClaim } from './services/cornerClaim'
import { confirmState, startConfirmation, resolveConfirmation, cancelConfirmation } from './services/confirmFlow'
import { shipEvent } from './logging'

const screens = {
  home: Home,
  newMatch: NewMatch,
  live: LiveMatch,
  pastMatches: PastMatches,
  pastMatchDetail: PastMatchDetail,
  players: Players,
  versus: Versus,
  player: Player,
  tournaments: Tournaments,
  tournamentDetail: TournamentDetail,
  voiceTraining: VoiceTraining,
  debug: DebugStt,
  debugTts: DebugTts,
} as const

const CurrentScreen = computed(() => screens[nav.screen])
const showChrome = computed(() => nav.screen !== 'live' && nav.screen !== 'newMatch')

let stopScanner: (() => void) | null = null
let didAutoJump = false
const initialHash = location.hash

// Round-23 (2026-08-31, live-testing fix): "I play on the blue side" said on Home was heard and
// classified fine (per the debug panel) but did NOTHING — corner_claim was only ever wired up in
// NewMatch.vue's own onSttCommand subscription, so a command fired on any other screen landed in
// a void with zero feedback. THE single onSttCommand hub, from now on: every classified command
// either (a) is owned by the current screen, which already gives its own dedicated feedback
// (NewMatch.vue's popup+glow for corner_claim, LiveMatch.vue's popup for score/win/point claims)
// — this hub stays silent there to avoid a double toast on top of that — or (b) gets a toast
// here, success when acted on, error/info when recognized but not applicable right now. Every
// non-applicable case also ships a track event (DESIGN.md §16 — "track everything"): a command
// that visibly did nothing is exactly the kind of thing that needs to be queryable later.
// guard_rejected/garbage never reaches onSttCommand listeners at all (stt.ts only calls them for
// real classified 'command' events), so there's no risk of toasting noise here.
function notApplicableMessage(cmd: SttCommandEvent): string {
  switch (cmd.command) {
    case 'corner_claim':
      return 'Comando reconocido, pero no hay partida en preparación'
    case 'nav_new_match':
      return 'Comando reconocido, pero hay una partida en curso'
    case 'score':
    case 'win_claim':
    case 'point_claim':
      return 'Comando reconocido, pero no hay ninguna partida en curso'
    default:
      return 'Comando reconocido, pero esa acción no está disponible todavía'
  }
}

// Round-27 (2026-08-31): the confirmation flow's cross-command rules live HERE, screen-
// independent — confirmState (services/confirmFlow.ts) is global, not owned by whichever screen
// started it, so this hub (not a per-screen handler) is the right place to gate 'yes_no' and to
// cancel a pending confirmation when anything else arrives.
//
//  - 'yes_no' commands (kmb_stt.py's grammar OR semanticIntent.ts's fallback — both are
//    STATELESS and classify a bare sí/no regardless of app state) ONLY resolve a confirmation
//    while one is actually pending; a bare "no" heard at any other time does NOTHING here — not
//    even a toast/track event, since ordinary conversation saying "no" near the table would
//    otherwise spam the eventstream and, worse, feel like the table is reacting to nothing.
//  - ANY other command arriving while a confirmation is pending cancels it first, THEN is
//    processed normally below — a fresh corner_claim naturally starts a NEW confirmation right
//    after (services/confirmFlow.ts's own replace guard is a second line of defense), giving a
//    "replace" outcome without a special case here; every other command (nav_new_match, score,
//    win/point claims) just cancels and proceeds untouched, since none of those make sense to
//    answer with while the table is mid-ask.
async function onVoiceCommand(cmd: SttCommandEvent) {
  const screen = nav.screen

  if (cmd.command === 'yes_no') {
    if (confirmState.status === 'awaiting') resolveConfirmation(cmd.answer === 'no' ? 'no' : 'yes', 'voice')
    return
  }
  if (confirmState.status === 'awaiting') cancelConfirmation()

  if (cmd.command === 'nav_new_match') {
    // Deliberately a no-op while a game is actually live — voice during a live match is
    // score/claim territory (LiveMatch.vue's own handler), and jumping the table away from an
    // in-progress game because of a misheard "new match" would be a bad failure mode.
    if (screen === 'live') {
      shipEvent('command-not-applicable', { command: cmd.command, raw: cmd.raw, screen, source: cmd.source })
      pushToast('error', notApplicableMessage(cmd))
      return
    }
    navigate('newMatch')
    pushToast('success', 'Nueva partida')
    return
  }

  if (cmd.command === 'corner_claim') {
    // NewMatch.vue owns this screen's corner-claim UI already (popup + card glow) — stay quiet
    // so a claim there doesn't ALSO produce a toast on top of it.
    if (screen === 'newMatch') return
    if (screen !== 'home') {
      shipEvent('command-not-applicable', { command: cmd.command, raw: cmd.raw, screen, color: cmd.color, source: cmd.source })
      pushToast('error', notApplicableMessage(cmd))
      return
    }
    // Home (2026-08-31 fix): a claim here now DOES something useful, consistent with the
    // existing "2nd player picked -> jump to New match" auto-advance (Home.vue's onPick /
    // DESIGN.md 9.1 round-3 item 1) — resolve the speaker, pre-assign them to the claimed side
    // in the shared draft, and carry straight into New match for format/best-of/Start.
    const result = await resolveCornerClaim(cmd, 'home')
    if (!result) return
    const side = result.color === 'red' ? 'campo rojo' : 'campo azul'
    if (result.outcome === 'accepted' && result.player) {
      setSide(result.side, result.player)
      navigate('newMatch')
      pushToast('success', `${side}: ${result.player.displayName} ${result.confidencePct}%`, result.player)
      return
    }
    // Round-27: 'dubious' band — ask instead of silently assigning or dropping. The confirm
    // prompt is global (ConfirmClaimPrompt.vue, mounted below); only the eventual 'confirmed'
    // outcome applies anything.
    if (result.outcome === 'needs_confirmation' && result.player) {
      const player = result.player
      startConfirmation({
        cmd,
        screen: 'home',
        result,
        scope: result.scopeLabel,
        command: 'corner_claim',
        onResolve: (resolution, resolvedPlayer) => {
          if (resolution === 'confirmed') {
            setSide(result.side, resolvedPlayer)
            navigate('newMatch')
            pushToast('success', `${side}: ${resolvedPlayer.displayName} (confirmado)`, resolvedPlayer)
          } else if (resolution === 'denied') {
            pushToast('error', `Vale, no es ${player.displayName} — prueba de nuevo`)
          }
          // timeout/cancelled: silent, per the standing false-negative-biased design.
        },
      })
      return
    }
    const errorMessage =
      result.outcome === 'rejected_duplicate_side' && result.other
        ? `${result.other.displayName} ya está en el otro campo`
        : result.outcome === 'no_fingerprint'
          ? 'Voz reconocida, pero sin huella para identificar al jugador (entrena tu voz primero)'
          : result.outcome === 'below_confidence_threshold'
            ? 'Voz no reconocida — elige el jugador manualmente'
            : 'Voz reconocida, pero no se pudo cargar el perfil del jugador'
    pushToast('error', errorMessage)
    return
  }

  if (cmd.command === 'score' || cmd.command === 'win_claim' || cmd.command === 'point_claim') {
    // LiveMatch.vue owns these on the live screen (its own popup) — stay quiet there.
    if (screen === 'live') return
    shipEvent('command-not-applicable', { command: cmd.command, raw: cmd.raw, screen, source: cmd.source })
    pushToast('error', notApplicableMessage(cmd))
    return
  }

  // 'start' / 'end' — classified server-side (kmb_stt.py) but never wired to any client action;
  // still surface that the table heard something rather than staying silent about it.
  shipEvent('command-not-applicable', { command: cmd.command, raw: cmd.raw, screen, color: cmd.color, source: cmd.source })
  pushToast('error', notApplicableMessage(cmd))
}

let stopVoiceHub: (() => void) | null = null
onMounted(() => {
  stopVoiceHub = onSttCommand((cmd) => void onVoiceCommand(cmd))
})
onUnmounted(() => stopVoiceHub?.())

// Round-9 (2026-08-31): every button click gets audio feedback — a single capture-phase
// listener here instead of wiring sounds.tap() into each of the app's dozens of buttons.
// Fires only for enabled <button>s (disabled ones don't dispatch click at all, so they stay
// correctly silent). The tap tick is deliberately tiny and duller than the semantic chimes
// (point/gameWin/…) that often follow the same press, so the two read as press + result.
function onGlobalClick(e: MouseEvent) {
  if ((e.target as HTMLElement | null)?.closest('button')) sounds.tap()
}

onMounted(() => {
  startTableStream()
  stopScanner = useGlobalScanner()
  document.addEventListener('click', onGlobalClick, true)
})

onUnmounted(() => {
  stopScanner?.()
  document.removeEventListener('click', onGlobalClick, true)
})

// "A reload while a match is live lands on Live" (DESIGN.md section 9.2) — only on the very
// first state tick, and only if the page loaded with no deep link of its own.
watch(
  () => tableStore.state,
  (state) => {
    if (didAutoJump || !state) return
    didAutoJump = true
    if (state.liveMatch && !initialHash) {
      replaceScreen('live', { matchId: state.liveMatch.id })
    }
  },
)

// Round-27: navigating away cancels a pending confirmation (requirement — "cancellation when the
// user navigates away") — a confirm prompt whose originating screen is no longer current has
// nothing sensible left to resolve into (setSide targets a draft the player isn't looking at
// anymore). A confirmation's OWN successful resolution navigates itself (Home's onResolve above)
// — that navigate() call always runs AFTER confirmFlow.ts has already flipped confirmState back
// to 'idle' (finish() runs synchronously before calling onResolve), so this watcher never
// self-cancels the confirmation it's reacting to.
watch(
  () => nav.screen,
  () => {
    if (confirmState.status === 'awaiting') cancelConfirmation()
  },
)
</script>

<template>
  <!-- Round-14: w-full + h-dvh, not w-screen/h-screen — 100vw includes the scrollbar width on
       desktop (horizontal overflow on a Mac) and 100vh can mis-measure vs the real window; dvh
       tracks the actual visible viewport on every platform. -->
  <div class="h-dvh w-full flex flex-col overflow-hidden bg-neutral-950 text-neutral-100">
    <BackendBanner />
    <NavHeader v-if="showChrome" />
    <component :is="CurrentScreen" />
    <ToastStack />
    <ConfirmClaimPrompt />
  </div>
</template>
